// packages/main/app/web/components-v2/UnifiedImageDialog/AIImageChat.tsx

import { useQueryClient } from '@tanstack/react-query';
import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { useEventLogging } from '../../../models/Logging';
import { useAIAssistantCreateConversationMutation } from '../../../queries/AI/useAIAssistantCreateConversationMutation';
import {
	getConversationChatMessageQueryKey,
	useAIConversationChatMessageQuery,
} from '../../../queries/AIConversation/useAIConversationChatMessageQuery';
import { useImageAttachmentMutation } from '../../../queries/Attachments/useImageAttachmentMutation';
import { useGetSystemJobQuery } from '../../../queries/SystemJob/useGetSystemJobQuery';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { WarningIcon } from '../../components/svgs/icons/WarningIcon';
import {
	AIChatAssistantMessage,
	removeErrorMessageFromConversationCache,
	useAIAssistantStreamingMutation,
} from '../../containers/templates/AssistantChat';
import { bs } from '../../styles/styles';
import { AIConversationMessage } from '../ai/AIConversationMessage';
import { ChatConversation, ChatConversationContent, ChatConversationScrollButton } from '../ai/ChatConversation';
import { ChatInput } from '../ai/ChatInput';
import { ImageAttachment } from '../ai/ImageAttachmentManager';
import { Button } from '../Button';
import { s } from './styles';

const IMAGE_ASSISTANT_NAME = 'ImageGenerationAgent';

interface MediaContentDisplayProps {
	toolCall: Api.MediaContentToolCall;
	onJobComplete?: () => void;
	onUseImage?: (fileAttachments: Api.IFileAttachment[]) => void;
}

export function MediaContentDisplay({ toolCall, onJobComplete, onUseImage }: MediaContentDisplayProps) {
	const [hasNotifiedCompletion, setHasNotifiedCompletion] = React.useState(false);

	const systemJobQuery = useGetSystemJobQuery<Api.ISystemJob & { fileAttachments?: Api.IFileAttachment[] }>({
		enabled: Boolean(toolCall.systemJobId),
		jobId: toolCall.systemJobId,
		refetchInterval: query => {
			const data = query.state.data;
			if (!toolCall.systemJobId) {
				return false;
			}
			// Poll every 3 seconds until job completes
			return data?.percentComplete !== 100 ? 3000 : false;
		},
		refetchOnWindowFocus: false,
	});

	React.useEffect(() => {
		if (
			systemJobQuery.data?.percentComplete === 100 &&
			systemJobQuery.data?.status === 'Success' &&
			!hasNotifiedCompletion
		) {
			setHasNotifiedCompletion(true);
			onJobComplete?.();
		}
	}, [systemJobQuery.data?.percentComplete, hasNotifiedCompletion, onJobComplete, systemJobQuery.data?.status]);

	// If job is complete and successful, show the generated images
	if (systemJobQuery.data?.percentComplete === 100 && systemJobQuery.data?.status === 'Success') {
		const fileAttachments = systemJobQuery.data.fileAttachments || toolCall.fileAttachments;
		if (fileAttachments && fileAttachments.length > 0) {
			return (
				<>
					<div className={css(bs.flex, bs.gap3, bs.flexWrap, bs.mt3)}>
						{fileAttachments.map((attachment, idx) => (
							<img
								key={`generated-${toolCall.id}-${idx}`}
								src={attachment.url}
								alt={attachment.fileName || 'Generated image'}
								style={{
									maxWidth: '400px',
									maxHeight: '400px',
									borderRadius: '12px',
									border: '1px solid var(--color-border-main)',
									objectFit: 'contain',
									boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
								}}
							/>
						))}
					</div>
					{onUseImage && (
						<div className={css(bs.flex, bs.justifyCenter, bs.mt3, bs.mb3)}>
							<Button variant='primary' onClick={() => onUseImage(fileAttachments)}>
								Use This Image
							</Button>
						</div>
					)}
				</>
			);
		}
	}

	// Job complete but failed or no attachments = error
	if (systemJobQuery.data?.percentComplete === 100 && systemJobQuery.data?.status !== 'Success') {
		return (
			<div
				className={css(bs.flex, bs.itemsCenter, bs.gap2, bs.p3, bs.mt2)}
				style={{
					backgroundColor: '#fef2f2',
					borderRadius: '8px',
					fontSize: '14px',
					color: '#dc2626',
				}}
			>
				<WarningIcon />
				<span>Failed to generate image</span>
			</div>
		);
	}

	// Show loading state while processing
	return (
		<div
			className={css(bs.flex, bs.itemsCenter, bs.gap2, bs.p3, bs.mt2)}
			style={{
				backgroundColor: '#f8fafc',
				borderRadius: '8px',
				fontSize: '14px',
				color: '#64748b',
			}}
		>
			<LoadingSpinner type='tiny' />
			<span>Generating image...</span>
		</div>
	);
}

interface AIImageChatProps {
	initialImages?: Api.IFileAttachment[];
	existingConversationId?: string;
	onImageSelected?: (fileAttachments: Api.IFileAttachment[]) => void;
	onConversationCreated?: (conversationId: string) => void;
	impersonationContext?: Api.IImpersonationContext;
	assistantName?: string;
}

const useImageAssistantConversation = (
	isOpen: boolean,
	assistantName: string,
	impersonationContext?: Api.IImpersonationContext,
	existingConversationId?: string,
	onConversationCreated?: (conversationId: string) => void
) => {
	const logger = useEventLogging('useImageAssistantConversation');
	const aiAssistantCreateConversationMutation = useAIAssistantCreateConversationMutation({
		onError: error => {
			logger.logApiError(`useImageAssistantConversation-error`, error);
			toast.error('Failed to create conversation');
		},
		onSuccess: data => {
			if (data?.id) {
				onConversationCreated?.(data.id);
			}
		},
	});

	const aiConversationChatMessageQuery = useAIConversationChatMessageQuery({
		enabled: Boolean(existingConversationId || aiAssistantCreateConversationMutation.data),
		id: existingConversationId || aiAssistantCreateConversationMutation.data?.id,
		impersonationContext,
		staleTime: Infinity,
	});

	React.useEffect(() => {
		// Skip creating a new conversation if we already have one
		if (existingConversationId) {
			return;
		}

		if (isOpen && aiAssistantCreateConversationMutation.isIdle && !aiAssistantCreateConversationMutation.data) {
			aiAssistantCreateConversationMutation.mutate({
				assistantName,
				impersonationContext,
			});
		}
	}, [aiAssistantCreateConversationMutation, isOpen, impersonationContext, assistantName, existingConversationId]);

	return {
		conversationId: existingConversationId || aiAssistantCreateConversationMutation.data?.id,
		aiConversationChatMessageQuery,
		isLoadingConversation: !existingConversationId && aiAssistantCreateConversationMutation.isPending,
	};
};

export const AIImageChat = ({
	initialImages,
	existingConversationId,
	onImageSelected,
	onConversationCreated,
	impersonationContext,
	assistantName,
}: AIImageChatProps) => {
	const id = React.useId();
	const queryClient = useQueryClient();
	const logger = useEventLogging('AIImageChat');
	const effectiveAssistantName = assistantName || IMAGE_ASSISTANT_NAME;
	const [convertedInitialImages, setConvertedInitialImages] = React.useState<ImageAttachment[]>([]);

	const { conversationId, aiConversationChatMessageQuery, isLoadingConversation } = useImageAssistantConversation(
		true, // Always consider "open" since this is just the chat UI component
		effectiveAssistantName,
		impersonationContext,
		existingConversationId,
		onConversationCreated
	);

	// Convert IFileAttachment to ImageAttachment format
	React.useEffect(() => {
		if (!initialImages || initialImages.length === 0) {
			setConvertedInitialImages([]);
			return;
		}

		// Create ImageAttachment objects using the existing file attachment data
		// We create a minimal File object just to satisfy the interface, but we'll primarily use the existing attachment IDs
		const converted = initialImages.map((fileAttachment, index) => {
			// Create a minimal File object (won't be uploaded since we already have the attachment)
			const file = new File([], fileAttachment.fileName || `image-${index}`, {
				type: fileAttachment.mimeType || 'image/png',
			});

			return {
				id: fileAttachment.id,
				file,
				previewUrl: fileAttachment.url,
				name: fileAttachment.fileName || `image-${index}`,
				size: fileAttachment.fileSize || 0,
			} as ImageAttachment;
		});

		setConvertedInitialImages(converted);
	}, [initialImages]);

	const handleUseImage = React.useCallback(
		(fileAttachments: Api.IFileAttachment[]) => {
			onImageSelected?.(fileAttachments);
			toast.success('Image added');
		},
		[onImageSelected]
	);

	const aiAssistantStreamingMutation = useAIAssistantStreamingMutation({
		assistantName: effectiveAssistantName,
	});

	const fileAttachmentMutation = useImageAttachmentMutation({
		onError: error => {
			logger.logApiError('AIImageChat-upload-error', error);
			toast.error('Failed to upload images. Please try again.');
		},
	});

	const handleTryAgain = (chatRequest: Api.AIChatRequest) => {
		if (!conversationId) {
			return;
		}
		removeErrorMessageFromConversationCache(conversationId, impersonationContext);
		aiAssistantStreamingMutation.mutate({
			conversationId,
			chatRequest,
			impersonationContext,
		});
	};

	const handlePromptSubmit = React.useCallback(
		async (prompt: string, images?: ImageAttachment[]) => {
			if (!conversationId) {
				toast.error('Conversation not ready. Please try again.');
				return;
			}

			try {
				removeErrorMessageFromConversationCache(conversationId, impersonationContext);

				// Handle images: separate existing attachments from new uploads
				const fileAttachments: Api.IFileAttachment[] = [];
				if (images && images.length > 0) {
					// Separate images that already have IDs (from initialImages) from new uploads
					const existingAttachments = images.filter(img => img.id && img.file.size === 0);
					const newUploads = images.filter(img => !img.id || img.file.size > 0);

					// Use existing attachments directly
					if (existingAttachments.length > 0 && initialImages) {
						const existingFileAttachments = initialImages.filter(fa => existingAttachments.some(ea => ea.id === fa.id));
						fileAttachments.push(...existingFileAttachments);
					}

					// Upload new images
					if (newUploads.length > 0) {
						const uploadResult = await fileAttachmentMutation.mutateAsync({
							files: newUploads.map(img => img.file),
							impersonationContext,
						});
						fileAttachments.push(...uploadResult);
					}
				}

				// Add user message to cache (with fileAttachments for display purposes only)
				queryClient.setQueryData(
					getConversationChatMessageQueryKey({
						id: conversationId,
						impersonationContext,
					}),
					(prevMessages: Api.AIChatMessage[]) => [
						...(prevMessages ?? []),
						{
							role: Api.AIChatRole.User,
							text: prompt,
							id: null,
							fileAttachments: fileAttachments.length > 0 ? fileAttachments : undefined,
						},
					]
				);

				// Send to API with MediaAIContext containing file attachment IDs
				const chatRequest: Api.AIMessageRequest = {
					chatMessages: [
						{
							role: Api.AIChatRole.User,
							text: prompt,
							id: null,
						},
					],
					contexts:
						fileAttachments.length > 0
							? [
									{
										_type: 'MediaAIContext',
										fileAttachmentIds: fileAttachments.map(attachment => attachment.id),
									} as Api.MediaAIContext,
								]
							: undefined,
				};

				aiAssistantStreamingMutation.mutate({
					conversationId,
					chatRequest,
					impersonationContext,
				});
			} catch (error) {
				logger.logApiError('AIImageChat-submit-error', error as Api.IOperationResultNoValue);
				toast.error('Failed to send message. Please try again.');
			}
		},
		[
			conversationId,
			impersonationContext,
			initialImages,
			fileAttachmentMutation,
			queryClient,
			aiAssistantStreamingMutation,
			logger,
		]
	);

	if (isLoadingConversation || !conversationId) {
		return (
			<div className={css(bs.flex, bs.itemsCenter, bs.justifyCenter, bs.hFull)}>
				<LoadingSpinner />
			</div>
		);
	}

	return (
		<div className={css(s.chatContainer)}>
			<ChatConversation styles={[s.chatMessages]}>
				<ChatConversationContent>
					<AIChatAssistantMessage>
						Hey! I can help you generate or modify images. Upload an image and tell me what you&apos;d like to do with
						it, or just describe an image you&apos;d like me to create.
					</AIChatAssistantMessage>
					{aiConversationChatMessageQuery.data?.map((message, i) => {
						return (
							<AIConversationMessage
								key={`${id}-assistant-message-${message.id ?? i}`}
								message={message}
								onUseImage={onImageSelected ? handleUseImage : undefined}
								onImageGenJobComplete={() => aiConversationChatMessageQuery.refetch()}
								onErrorTryAgain={handleTryAgain}
							/>
						);
					})}
					{aiAssistantStreamingMutation.isLoading && (
						<AIChatAssistantMessage>
							<LoadingSpinner type='tiny' />
						</AIChatAssistantMessage>
					)}
				</ChatConversationContent>
				<ChatConversationScrollButton />
			</ChatConversation>

			<div className={css(s.chatInputContainer)}>
				<ChatInput
					streamingInProgress={aiAssistantStreamingMutation.isLoading || aiAssistantStreamingMutation.isStreaming}
					placeholder='Describe the image you want...'
					onSubmit={handlePromptSubmit}
					supportsImages={true}
					maxImages={2}
					initialImages={convertedInitialImages}
				/>
			</div>
		</div>
	);
};
